import { create } from 'zustand';
import { serverAPI } from '../lib/api';
import { joinServer, getChannel, joinServerChannel } from '../lib/realtime';
import { v4 as uuidv4 } from 'uuid';

interface Server {
  id: string;
  name: string;
  description: string | null;
  icon_url: string | null;
  owner_id: string;
  is_banned?: number;
  ban_reason?: string | null;
  created_at: string;
}

interface Channel {
  id: string;
  name: string;
  description: string | null;
  server_id: string;
  parent_id?: string | null;
  type: string;
  is_private: boolean;
  position?: number;
  permissions?: number;
  permission_sync_state?: 'synced' | 'out_of_sync' | null;
  created_at: string;
}

type FetchOptions = {
  silent?: boolean;
};

const isDev = import.meta.env.DEV;

interface ServerMember {
  id: string;
  server_id: string;
  user_id: string;
  role?: string;
  roles: { id: string; name: string; color: string | null; permissions: number; position: number; is_everyone?: boolean; hoist?: boolean }[];
  created_at: string;
  user?: {
    display_name?: string | null;
    username: string;
    avatar_url: string | null;
    status: string | null;
    bot?: boolean;
    is_system?: boolean;
  };
}

interface ServerRole {
  id: string;
  name: string;
  color: string | null;
  permissions: any;
  server_id?: string;
  position?: number;
  is_everyone?: boolean;
  hoist?: boolean;
}

interface ServerState {
  servers: Server[];
  currentServer: Server | null;
  channels: Channel[];
  currentChannel: Channel | null;
  joinServer: (serverId: string) => Promise<any>;
  members: ServerMember[];
  roles: ServerRole[];
  isLoading: boolean;
  error: string | null;
  isServerReady: boolean;
  setServerReady: (ready: boolean) => void;
  
  fetchUserServers: () => Promise<void>;
  fetchServerChannels: (serverId: string, options?: FetchOptions) => Promise<void>;
  fetchServerMembers: (serverId: string, options?: FetchOptions) => Promise<void>;
  fetchServerRoles: (serverId: string, options?: FetchOptions) => Promise<void>;
  createServer: (name: string, description?: string, iconUrl?: string) => Promise<Server | null>;
  updateServer: (serverId: string, updates: any) => Promise<Server | null>;
  createChannel: (name: string, serverId: string, type?: string, isPrivate?: boolean, description?: string | null, parentId?: string | null) => Promise<Channel | null>;
  updateChannel: (channelId: string, updates: any) => Promise<void>;
  deleteChannel: (channelId: string) => Promise<void>;
  reorderChannels: (serverId: string, items: Array<{ id: string; parent_id?: string | null; position: number; sync_permissions?: boolean }>) => Promise<void>;
  syncChannelPermissions: (channelId: string) => Promise<void>;
  createRole: (serverId: string, name: string, color?: string, permissions?: any) => Promise<ServerRole | null>;
  updateRole: (serverId: string, roleId: string, updates: any) => Promise<ServerRole | null>;
  deleteRole: (serverId: string, roleId: string) => Promise<void>;
  assignRole: (serverId: string, userId: string, roleId: string) => Promise<void>;
  removeRole: (serverId: string, userId: string, roleId: string) => Promise<void>;
  updateMemberRole: (serverId: string, userId: string, role: string) => Promise<void>;
  leaveServer: (serverId: string) => Promise<void>;
  setCurrentServer: (server: Server | null) => void;
  setCurrentChannel: (channel: Channel | null) => void;
  subscribeToServerEvents: () => () => void;
}

export const useServerStore = create<ServerState>((set, get) => ({
  servers: [],
  currentServer: null,
  channels: [],
  currentChannel: null,
  members: [],
  roles: [],
  isLoading: false,
  error: null,
  isServerReady: false,
  
  fetchUserServers: async () => {
    try {
      set({ isLoading: true, error: null });
      
      // Get user's servers from API
      const servers = await serverAPI.getServers();
      
      set({ servers: servers || [] });
    } catch (error: any) {
      console.error('Error fetching servers:', error);
      set({ error: error.response?.data?.message || error.message || 'Failed to fetch servers' });
    } finally {
      set({ isLoading: false });
    }
  },
  
  fetchServerChannels: async (serverId, options = {}) => {
    try {
      if (!options.silent) set({ isLoading: true, error: null });
      
      // Get server channels from API
      const channels = await serverAPI.getChannels(serverId);
      const nextChannels = channels || [];
      const current = get().currentChannel;
      const refreshedCurrent = current ? nextChannels.find((channel: Channel) => channel.id === current.id) : null;
      const shouldReconcileCurrent = !current || current.server_id === serverId;
      const nextCurrentChannel = refreshedCurrent || (shouldReconcileCurrent ? null : current);

      set({
        channels: nextChannels,
        currentChannel: nextCurrentChannel,
      });

      if (isDev && current && current.server_id === serverId && !refreshedCurrent) {
        console.debug('[serverStore] Current channel is no longer visible; switched to server home.', {
          previousChannelId: current.id,
        });
      }
    } catch (error: any) {
      console.error('Error fetching channels:', error);
      set({ error: error.response?.data?.message || error.message || 'Failed to fetch channels' });
    } finally {
      if (!options.silent) set({ isLoading: false });
    }
  },
  
  fetchServerMembers: async (serverId, options = {}) => {
    try {
      if (!options.silent) set({ isLoading: true, error: null });
      
      // Get server members from API
      const members = await serverAPI.getServerMembers(serverId);
      
      set({ members: members || [] });
    } catch (error: any) {
      console.error('Error fetching members:', error);
      set({ error: error.response?.data?.message || error.message || 'Failed to fetch server members' });
    } finally {
      if (!options.silent) set({ isLoading: false });
    }
  },
  
  fetchServerRoles: async (serverId, options = {}) => {
    try {
      if (!options.silent) set({ isLoading: true, error: null });
      
      // Get server roles from API
      // Using a try-catch block to handle potential errors
      try {
        const roles = await serverAPI.getRoles(serverId);
        set({ roles: roles || [] });
      } catch (err) {
        // If the API call fails, set an empty array for roles
        console.warn('Could not fetch server roles, using empty array instead');
        set({ roles: [] });
      }
    } catch (error: any) {
      console.error('Error in fetchServerRoles:', error);
      // Use a plain string error message to avoid potential Symbol issues
      set({ error: 'Failed to fetch server roles' });
    } finally {
      if (!options.silent) set({ isLoading: false });
    }
  },
  
  createServer: async (name, description, iconUrl) => {
    try {
      set({ isLoading: true, error: null });
      
      // Create server with API
      const server = await serverAPI.createServer(name, description, iconUrl);
      
      // Update servers list
      const updatedServers = [...get().servers, server];
      set({ 
        servers: updatedServers, 
        currentServer: server
      });
      
      // Fetch channels for the new server
      await get().fetchServerChannels(server.id);
      
      return server;
    } catch (error: any) {
      console.error('Error creating server:', error);
      set({ error: error.response?.data?.message || error.message || 'Failed to create server' });
      return null;
    } finally {
      set({ isLoading: false });
    }
  },
  
  updateServer: async (serverId, updates) => {
    try {
      set({ isLoading: true, error: null });
      
      // Update server with API
      const server = await serverAPI.updateServer(serverId, updates);
      
      // Update servers list and current server
      const updatedServers = get().servers.map(s => 
        s.id === serverId ? server : s
      );
      
      set({ 
        servers: updatedServers,
        currentServer: get().currentServer?.id === serverId ? server : get().currentServer
      });
      
      return server;
    } catch (error: any) {
      console.error('Error updating server:', error);
      set({ error: error.response?.data?.message || error.message || 'Failed to update server' });
      return null;
    } finally {
      set({ isLoading: false });
    }
  },
  
  createChannel: async (name, serverId, type = 'text', isPrivate = false, description = null, parentId = null) => {
    try {
      set({ isLoading: true, error: null });
      
      // Create channel with API
      const channel = await serverAPI.createChannel(serverId, name, description || undefined, type, isPrivate, parentId);
      
      await get().fetchServerChannels(serverId, { silent: true });
      const created = get().channels.find(c => c.id === channel.id) || channel;
      if (created.type !== 'category') {
        set({ currentChannel: created });
      }
      
      return created;
    } catch (error: any) {
      console.error('Error creating channel:', error);
      set({ error: error.response?.data?.message || error.message || 'Failed to create channel' });
      return null;
    } finally {
      set({ isLoading: false });
    }
  },

  updateChannel: async (channelId, updates) => {
    try {
      set({ isLoading: true, error: null });
      await serverAPI.updateChannel(channelId, updates);
      const serverId = get().currentServer?.id;
      if (serverId) await get().fetchServerChannels(serverId, { silent: true });
    } catch (error: any) {
      console.error('Error updating channel:', error);
      set({ error: error.response?.data?.message || error.message || 'Failed to update channel' });
    } finally {
      set({ isLoading: false });
    }
  },

  deleteChannel: async (channelId) => {
    try {
      set({ isLoading: true, error: null });
      await serverAPI.deleteChannel(channelId);
      const serverId = get().currentServer?.id;
      if (serverId) await get().fetchServerChannels(serverId, { silent: true });
      if (get().currentChannel?.id === channelId) {
        set({ currentChannel: null });
      }
    } catch (error: any) {
      console.error('Error deleting channel:', error);
      set({ error: error.response?.data?.message || error.message || 'Failed to delete channel' });
    } finally {
      set({ isLoading: false });
    }
  },

  reorderChannels: async (serverId, items) => {
    try {
      set({ error: null });
      await serverAPI.reorderChannels(serverId, items);
      await get().fetchServerChannels(serverId, { silent: true });
    } catch (error: any) {
      console.error('Error reordering channels:', error);
      set({ error: error.response?.data?.message || error.message || 'Failed to reorder channels' });
    }
  },

  syncChannelPermissions: async (channelId) => {
    try {
      set({ isLoading: true, error: null });
      await serverAPI.syncChannelPermissions(channelId);
      const serverId = get().currentServer?.id;
      if (serverId) await get().fetchServerChannels(serverId, { silent: true });
    } catch (error: any) {
      console.error('Error syncing channel permissions:', error);
      set({ error: error.response?.data?.message || error.message || 'Failed to sync channel permissions' });
    } finally {
      set({ isLoading: false });
    }
  },
  
  createRole: async (serverId, name, color = '#f0f0f0', permissions = {}) => {
    try {
      set({ isLoading: true, error: null });
      
      // Create role with API
      const role = await serverAPI.createRole(serverId, name, color, permissions);
      
      // Update roles list
      const updatedRoles = [...get().roles, role];
      set({ roles: updatedRoles });
      
      return role;
    } catch (error: any) {
      console.error('Error creating role:', error);
      set({ error: error.response?.data?.message || error.message || 'Failed to create role' });
      return null;
    } finally {
      set({ isLoading: false });
    }
  },
  
  updateRole: async (serverId, roleId, updates) => {
    try {
      set({ isLoading: true, error: null });
      
      // Update role with API
      const role = await serverAPI.updateRole(serverId, roleId, updates);
      
      // Update roles list
      const updatedRoles = get().roles.map(r => 
        r.id === roleId ? role : r
      );
      
      set({ roles: updatedRoles });
      
      return role;
    } catch (error: any) {
      console.error('Error updating role:', error);
      set({ error: error.response?.data?.message || error.message || 'Failed to update role' });
      return null;
    } finally {
      set({ isLoading: false });
    }
  },
  
  deleteRole: async (serverId, roleId) => {
    try {
      set({ isLoading: true, error: null });
      
      // Delete role with API
      await serverAPI.deleteRole(serverId, roleId);
      
      // Update roles list
      const updatedRoles = get().roles.filter(r => r.id !== roleId);
      set({ roles: updatedRoles });
    } catch (error: any) {
      console.error('Error deleting role:', error);
      set({ error: error.response?.data?.message || error.message || 'Failed to delete role' });
    } finally {
      set({ isLoading: false });
    }
  },
  
  assignRole: async (serverId, userId, roleId) => {
    try {
      set({ isLoading: true, error: null });
      
      // Assign role with API
      await serverAPI.assignRole(serverId, userId, roleId);
      
      // Refresh members to get updated roles
      await get().fetchServerMembers(serverId);
    } catch (error: any) {
      console.error('Error assigning role:', error);
      set({ error: error.response?.data?.message || error.message || 'Failed to assign role' });
    } finally {
      set({ isLoading: false });
    }
  },
  
  removeRole: async (serverId, userId, roleId) => {
    try {
      set({ isLoading: true, error: null });
      
      // Remove role with API
      await serverAPI.removeRole(serverId, userId, roleId);
      
      // Refresh members to get updated roles
      await get().fetchServerMembers(serverId);
    } catch (error: any) {
      console.error('Error removing role:', error);
      set({ error: error.response?.data?.message || error.message || 'Failed to remove role' });
    } finally {
      set({ isLoading: false });
    }
  },
  
  updateMemberRole: async (serverId, userId, role) => {
    try {
      set({ isLoading: true, error: null });
      
      // Update member role with API
      await serverAPI.updateMemberRole(serverId, userId, role);
      
      // Refresh members to get updated roles
      await get().fetchServerMembers(serverId);
    } catch (error: any) {
      console.error('Error updating member role:', error);
      set({ error: error.response?.data?.message || error.message || 'Failed to update member role' });
    } finally {
      set({ isLoading: false });
    }
  },

  // Ajouter ou mettre à jour cette méthode dans le serverStore

joinServer: async (serverId) => {
  try {
    set({ isLoading: true, error: null });
    
    // Rejoindre le serveur via l'API
    console.log(`Joining server with ID: ${serverId}`);
    const response = await serverAPI.joinServer(serverId);
    const server = response;
    
    console.log('Server joined successfully:', server);
    
    // Ajouter le serveur à la liste
    set(state => ({
      servers: [...state.servers, server],
      currentServer: server
    }));
    
    // Récupérer les canaux du nouveau serveur
    await get().fetchServerChannels(serverId);
    
    return server;
  } catch (error: any) {
    console.error('Error joining server:', error);
    set({ error: error.response?.data?.message || error.message || 'Failed to join server' });
    throw error;
  } finally {
    set({ isLoading: false });
  }
},

  leaveServer: async (serverId) => {
    try {
      const { useAuthStore } = await import('./authStore');
      const userId = useAuthStore.getState().user?.id;
      if (!userId) throw new Error('Not authenticated');
      
      await serverAPI.leaveServer(serverId, userId);
      
      set(state => ({
        servers: state.servers.filter(s => s.id !== serverId),
        currentServer: state.currentServer?.id === serverId ? null : state.currentServer,
        currentChannel: state.currentServer?.id === serverId ? null : state.currentChannel,
        channels: state.currentServer?.id === serverId ? [] : state.channels,
        members: state.currentServer?.id === serverId ? [] : state.members,
      }));
    } catch (error: any) {
      console.error('Error leaving server:', error);
      set({ error: error.response?.data?.message || error.message || 'Failed to leave server' });
      throw error;
    }
  },
  
  setCurrentServer: (server) => {
    // Indiquer le chargement dès qu'on change de serveur
    set({ 
      isLoading: true,
      currentServer: server
    });
    
    if (server) {
      // Join server socket room
      joinServer(server.id);
      
      // Fetch server data (Promise.all pour paralléliser et coordonner)
      Promise.all([
        get().fetchServerChannels(server.id),
        get().fetchServerMembers(server.id),
        get().fetchServerRoles(server.id).catch(err => {
          console.warn('Error fetching server roles:', err);
          return null; // Ne pas faire échouer le groupe de promesses
        })
      ]).finally(() => {
        // Marquer comme chargé une fois toutes les données récupérées
        set({ isLoading: false });
      });
    } else {
      set({ channels: [], members: [], roles: [], isLoading: false });
    }
  },
  
  setCurrentChannel: (channel) => {
    set({ currentChannel: channel });
  },
  
  setServerReady: (ready) => set({ isServerReady: ready }),
  
  subscribeToServerEvents: () => {
    const currentServer = get().currentServer;
    if (!currentServer) return () => {};
    
    const channel = getChannel(`server:${currentServer.id}`);
    if (!channel) {
      // Try joining the server channel first
      const ch = joinServerChannel(currentServer.id);
      if (!ch) return () => {};
      return get().subscribeToServerEvents(); // retry with the newly joined channel
    }
    
    // Set up event listeners on the Phoenix server channel
    const serverUpdateRef = channel.on('server_updated', () => {
      get().fetchUserServers();
    });
    
    const serverDeleteRef = channel.on('server_deleted', () => {
      get().fetchUserServers();
    });
    
    const channelCreateRef = channel.on('channel_created', (data: any) => {
      const serverId = data?.server_id || data?.serverId;
      if (get().currentServer && serverId === get().currentServer!.id) {
        get().fetchServerChannels(get().currentServer!.id, { silent: true });
      }
    });
    
    const channelUpdateRef = channel.on('channel_updated', (data: any) => {
      const serverId = data?.server_id || data?.serverId;
      if (get().currentServer && serverId === get().currentServer!.id) {
        get().fetchServerChannels(get().currentServer!.id, { silent: true });
      }
    });
    
    const channelDeleteRef = channel.on('channel_deleted', (data: any) => {
      const serverId = data?.server_id || data?.serverId;
      if (get().currentServer && serverId === get().currentServer!.id) {
        get().fetchServerChannels(get().currentServer!.id, { silent: true });
      }
    });
    
    const memberUpdateRef = channel.on('member_updated', (data: any) => {
      const serverId = data?.server_id || data?.serverId;
      if (get().currentServer && serverId === get().currentServer!.id) {
        get().fetchServerMembers(get().currentServer!.id, { silent: true });
      }
    });
    
    const memberRemoveRef = channel.on('member_removed', (data: any) => {
      const serverId = data?.server_id || data?.serverId;
      if (get().currentServer && serverId === get().currentServer!.id) {
        get().fetchServerMembers(get().currentServer!.id, { silent: true });
      }
    });

    const memberJoinRef = channel.on('member_joined', (data: any) => {
      const serverId = data?.server_id || data?.serverId;
      if (get().currentServer && serverId === get().currentServer!.id) {
        get().fetchServerMembers(get().currentServer!.id, { silent: true });
      }
    });

    const memberLeftRef = channel.on('member_left', (data: any) => {
      const serverId = data?.server_id || data?.serverId;
      if (get().currentServer && serverId === get().currentServer!.id) {
        get().fetchServerMembers(get().currentServer!.id, { silent: true });
      }
    });

    const permissionsUpdateRef = channel.on('permissions_updated', (data: any) => {
      const activeServer = get().currentServer;
      const eventServerId = data?.server_id || data?.serverId;
      if (!activeServer || (eventServerId && eventServerId !== activeServer.id)) return;

      if (isDev) {
        console.debug('[serverStore] permissions_updated received', {
          serverId: activeServer.id,
          reason: data?.reason,
        });
      }

      Promise.all([
        get().fetchServerChannels(activeServer.id, { silent: true }),
        get().fetchServerMembers(activeServer.id, { silent: true }),
        get().fetchServerRoles(activeServer.id, { silent: true }).catch(() => null),
      ])
        .then(() => {
          if (isDev) {
            console.debug('[serverStore] permissions state refreshed', {
              serverId: activeServer.id,
              channelCount: get().channels.length,
              memberCount: get().members.length,
              roleCount: get().roles.length,
            });
          }
        })
        .catch((err) => {
          console.warn('Failed to refresh permissions state:', err);
        });
    });
    
    // Return cleanup function
    return () => {
      channel.off('server_updated', serverUpdateRef);
      channel.off('server_deleted', serverDeleteRef);
      channel.off('channel_created', channelCreateRef);
      channel.off('channel_updated', channelUpdateRef);
      channel.off('channel_deleted', channelDeleteRef);
      channel.off('member_updated', memberUpdateRef);
      channel.off('member_removed', memberRemoveRef);
      channel.off('member_joined', memberJoinRef);
      channel.off('member_left', memberLeftRef);
      channel.off('permissions_updated', permissionsUpdateRef);
    };
  }
}));

// Initialize by fetching servers when token is available
if (localStorage.getItem('token')) {
  useServerStore.getState().fetchUserServers();
}
